import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';

import dayjs from 'dayjs';
import { Input, Button, DatePicker } from 'components/common';
import { useAuth, useGet, usePost } from 'hooks';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';
import {
  tradeshowCalendar,
  tradeshowCalendarYears,
  requestTradeshow,
  userKeywords,
} from 'services/api';
import {
  countries,
  states,
  tradeshowStatuses,
  tradeshowEventFormatOptions,
  tradeshowPlatformStatuses,
  registeredInterestOptions,
} from 'services/inputTokens';
import { getFilters } from 'selectors/calendar';
import { clearFilters, setFilter } from 'actions/calendar';
import { UpgradeLicense } from 'utils/constants';

import RequestTradeShow from './Modals/RequestTradeshow';
import Table from './Table';
import {
  Container,
  FilterContainer,
  Filters,
  FilterSection,
  FilterButtonGroup,
  FilterActions,
} from './styled';
import YearsMenu from './YearsMenu';
import Filter from './helpers/FilterField';
import { filterKeys } from './filterKeys';
import { TradeshowStatus } from 'models/tradeshowStatus';
import { isAfter } from 'utils/date';
import ExportTradeshowCalendarButton from './ActionButtons/ExportTradeshowCalendarButton';
import { formatMonthDate } from './helpers';

const filterData = (
  data,
  name,
  year,
  regionCountries,
  states,
  cities,
  tradeshowStatuses,
  tradeshowEventFormats,
  platformStatuses,
  registeredInterest,
  hasKeywords
) => {
  if (!data) return;
  let filteredData = data;

  //Tradeshow Statuses
  if (tradeshowStatuses && tradeshowStatuses.length > 0) {
    var now = new Date().toString();
    const getTradeshowStatus = tradeshow => {
      if (tradeshow.statusId === TradeshowStatus.Cancelled) return 'cancelled';
      if (tradeshow.statusId === TradeshowStatus.Postponed) {
        return 'postponed';
      }
      if (tradeshow.startDate && tradeshow.endDate) {
        // Normalize startDate to start of day, endDate to end of day
        const startDate = new Date(tradeshow.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(tradeshow.endDate);
        endDate.setHours(23, 59, 59, 999);

        if (isAfter(now, endDate)) return 'completed';
        if (isAfter(startDate, now)) return 'upcoming';
        return 'ongoing';
      }
      return 'tbc';
    };

    // Separate includes and excludes (excludes start with '-')
    const includes = tradeshowStatuses
      .filter(s => !String(s).startsWith('-'))
      .map(s => String(s).toLowerCase());
    const excludes = tradeshowStatuses
      .filter(s => String(s).startsWith('-'))
      .map(s =>
        String(s)
          .slice(1)
          .toLowerCase()
      );

    filteredData = filteredData.filter(r => {
      const status = String(getTradeshowStatus(r)).toLowerCase();
      const included = includes.length === 0 || includes.includes(status);
      const excluded = excludes.includes(status);
      return included && !excluded;
    });
  }

  //Platform Statuses
  if (platformStatuses && platformStatuses.length > 0) {
    const getPlatformStatus = tradeshow => {
      if (tradeshow.availableToDownload) return 'in platform';
      return 'pending';
    };

    // Separate includes and excludes (excludes start with '-')
    const includes = platformStatuses
      .filter(s => !String(s).startsWith('-'))
      .map(s => String(s).toLowerCase());
    const excludes = platformStatuses
      .filter(s => String(s).startsWith('-'))
      .map(s =>
        String(s)
          .slice(1)
          .toLowerCase()
      );

    filteredData = filteredData.filter(r => {
      const status = getPlatformStatus(r).toLowerCase();
      const included = includes.length === 0 || includes.includes(status);
      const excluded = excludes.includes(status);
      return included && !excluded;
    });
  }

  // Registered Interest
  if (registeredInterest && registeredInterest.length > 0) {
    const includes = registeredInterest
      .filter(s => !String(s).startsWith('-'))
      .map(s => String(s).toLowerCase());
    const excludes = registeredInterest
      .filter(s => String(s).startsWith('-'))
      .map(s =>
        String(s)
          .slice(1)
          .toLowerCase()
      );

    filteredData = filteredData.filter(r => {
      const isCurrentlyRegistered =
        r.isUserRegistered && !r.availableToDownload;

      const included =
        includes.length === 0 ||
        (includes.includes('currently registered interest') &&
          isCurrentlyRegistered) ||
        (includes.includes('all registered interest') && r.isUserRegistered);

      const excluded =
        (excludes.includes('currently registered interest') &&
          isCurrentlyRegistered) ||
        (excludes.includes('all registered interest') && r.isUserRegistered);

      return included && !excluded;
    });
  }

  // Tradeshow Event Formats (in-person, virtual, hybrid, tbc)
  if (tradeshowEventFormats && tradeshowEventFormats.length > 0) {
    const getEventFormat = tradeshow => {
      let inPerson = tradeshow.country?.length > 0;
      if (tradeshow.isTbc) {
        return 'tbc';
      } else if (inPerson && tradeshow.isVirtual) {
        return 'hybrid';
      } else if (inPerson) {
        return 'in-person';
      } else if (tradeshow.isVirtual) {
        return 'virtual';
      } else {
        return 'tbc';
      }
    };

    // Separate includes and excludes (excludes start with '-')
    const includes = tradeshowEventFormats
      .filter(s => !String(s).startsWith('-'))
      .map(s => String(s).toLowerCase());
    const excludes = tradeshowEventFormats
      .filter(s => String(s).startsWith('-'))
      .map(s =>
        String(s)
          .slice(1)
          .toLowerCase()
      );

    filteredData = filteredData.filter(r => {
      // Normalize event format for comparison
      const format = getEventFormat(r);
      const included = includes.length === 0 || includes.includes(format);
      const excluded = excludes.includes(format);
      return included && !excluded;
    });
  }

  if (name) {
    filteredData = filteredData.filter(r =>
      r.name.toLowerCase().includes(name.toLowerCase())
    );
  }
  if (year) {
    filteredData = filteredData.filter(
      r => r.year.toString() === year.toString()
    );
  }

  if (filteredData.length === 0) return [];

  const continents = [
    'africa',
    'asia',
    'europe',
    'north america',
    'south america',
    'oceania',
    'antarctica',
  ];

  const incRegions = [];
  const excRegions = [];
  const incCountries = [];
  const excCountries = [];

  (regionCountries || []).forEach(v => {
    let isExcluded = false;
    if (String(v).startsWith('-')) {
      v = v.slice(1);
      isExcluded = true;
    }

    // Check if the value is a continent or a country
    if (continents.includes(String(v).toLowerCase())) {
      if (isExcluded) {
        excRegions.push(v);
      } else {
        incRegions.push(v);
      }
    } else {
      if (isExcluded) {
        excCountries.push(v);
      } else {
        incCountries.push(v);
      }
    }
  });

  const incStates = (states || []).filter(s => !s.isExcluded);
  const excStates = (states || []).filter(s => s.isExcluded);

  const incCities = (cities || []).filter(v => !String(v).startsWith('-'));
  const excCities = (cities || [])
    .filter(v => String(v).startsWith('-'))
    .map(v => v.slice(1));

  let hasIncludes =
    incRegions.length > 0 ||
    incCountries.length > 0 ||
    incStates.length > 0 ||
    incCities.length > 0;

  if (hasIncludes) {
    filteredData = filteredData.filter(r => {
      // Normalize for case-insensitive comparison
      const region = r.region ? String(r.region).toLowerCase() : '';
      const country = r.country ? String(r.country).toLowerCase() : '';
      const city = r.city ? String(r.city).toLowerCase() : '';
      // incRegions, incCountries, incCities are as entered, so normalize them too
      const incRegionsLower = incRegions.map(v => String(v).toLowerCase());
      const incCountriesLower = incCountries.map(v => String(v).toLowerCase());
      const incCitiesLower = incCities.map(v => String(v).toLowerCase());
      const stateMatch =
        incStates.length === 0 || incStates.some(s => s.stateName === r.state);
      // OR logic for region/country
      const regionOrCountryMatch =
        (incRegionsLower.length === 0 && incCountriesLower.length === 0) ||
        incRegionsLower.includes(region) ||
        incCountriesLower.includes(country);
      return (
        regionOrCountryMatch &&
        stateMatch &&
        (incCitiesLower.length === 0 || incCitiesLower.includes(city))
      );
    });
  }

  if (filteredData.length === 0) return [];

  if (excRegions.length > 0) {
    filteredData = filteredData.filter(r => !excRegions.includes(r.region));
  }
  if (excCountries.length > 0) {
    filteredData = filteredData.filter(r => !excCountries.includes(r.country));
  }
  if (excStates.length > 0) {
    filteredData = filteredData.filter(
      r => !excStates.some(s => s.stateName === r.state)
    );
  }
  if (excCities.length > 0) {
    filteredData = filteredData.filter(r => !excCities.includes(r.city));
  }

  filteredData.forEach(d => (d['hasKeywords'] = hasKeywords));

  return filteredData;
};

const Calendar = () => {
  const { user } = useAuth();
  const filters = useSelector(getFilters);
  const {
    name: filterName,
    year: filterYear,
    month: filterMonth,
    startDate: filterStartDate,
    regionCountry: filterRegionCountries,
    state: filterStates,
    city: filterCities,
    tradeshowStatus: filterTradeshowStatus,
    platformStatus: filterPlatformStatus,
    tradeshowEventFormat: filterTradeshowEventFormat,
    registeredInterest: filterRegisteredInterest,
    noTbc: filterNoTbc,
    isVirtual: filterVirtual,
  } = filters;
  const dispatch = useDispatch();
  const [{ res: years, loadingYears }] = useGet({
    url: tradeshowCalendarYears,
    onMount: true,
  });
  const queryParams = {
    Year: filterYear,
    Month: formatMonthDate(filterYear, filterMonth),
  };

  if (filterStartDate && filterStartDate.length === 2) {
    queryParams.StartDate = new Date(filterStartDate[0])
      .toISOString()
      .split('T')[0];
    queryParams.EndDate = new Date(filterStartDate[1])
      .toISOString()
      .split('T')[0];
  }

  const [{ res, loading }] = useGet({
    url: tradeshowCalendar,
    query: queryParams,
    onMount: true,
  });

  const [
    { res: requestFinished, loading: reportLoading },
    postExport,
  ] = usePost({
    url: requestTradeshow,
  });

  useEffect(() => {
    if (requestFinished) {
      notification.open({
        message: 'Trade Show Request Sent',
        description: (
          <div>
            An email regarding your Trade Show Request has been sent to support
            to be processed.
          </div>
        ),
        placement: 'bottomLeft',
      });
    }
  }, [requestFinished]);

  const [displayTradeShowModal, setDisplayTradeShowModal] = useState(false);
  const [displayCustomDate, setDisplayCustomDate] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(filterStartDate);
  const [selectedYear, setSelectedYear] = useState(filterYear);
  const [selectedMonth, setSelectedMonth] = useState(filterMonth);

  // Determine if any additional filters are active
  const moreFiltersActive = useMemo(
    () =>
      (filterTradeshowStatus && filterTradeshowStatus.length > 0) ||
      (filterTradeshowEventFormat && filterTradeshowEventFormat.length > 0) ||
      (filterPlatformStatus && filterPlatformStatus.length > 0) ||
      (filterRegisteredInterest && filterRegisteredInterest.length > 0) ||
      filterNoTbc ||
      filterVirtual,
    [
      filterTradeshowStatus,
      filterTradeshowEventFormat,
      filterPlatformStatus,
      filterRegisteredInterest,
      filterNoTbc,
      filterVirtual,
    ]
  );

  // Initialize showMoreFilters based on moreFiltersActive
  const [showMoreFilters, setShowMoreFilters] = useState(
    moreFiltersActive || false
  );

  // If filters change and any are active, always show more filters
  useEffect(() => {
    if (moreFiltersActive) {
      setShowMoreFilters(true);
    }
  }, [moreFiltersActive]);

  const [{ res: keywords }] = useGet({
    url: userKeywords,
    onMount: true,
  });

  const filteredData = filterData(
    res,
    filterName,
    filterYear,
    filterRegionCountries,
    filterStates,
    filterCities,
    filterTradeshowStatus,
    filterTradeshowEventFormat,
    filterPlatformStatus,
    filterRegisteredInterest,
    keywords && keywords[0]
  );

  function onRequestNewTradeShowClick() {
    setDisplayTradeShowModal(true);
  }

  const handleYearSelect = year => {
    dispatch(setFilter(filterKeys.year.key, year));
    dispatch(setFilter(filterKeys.month.key, null));
    dispatch(setFilter(filterKeys.startDate.key, null));
    setDisplayCustomDate(false);
    setLocalDateRange(null);
  };

  const handleMonthSelect = (year, month) => {
    dispatch(setFilter(filterKeys.month.key, month));
    dispatch(setFilter(filterKeys.year.key, year));
    dispatch(setFilter(filterKeys.startDate.key, null));
    setDisplayCustomDate(false);
    setLocalDateRange(null);
  };

  const handleCustomSelect = () => {
    setDisplayCustomDate(true);
  };

  const handleClear = () => {
    setDisplayCustomDate(false);
    setSelectedYear(+dayjs().format('YYYY'));
    setSelectedMonth(null);
    setLocalDateRange(null);
    dispatch(clearFilters);
  };

  const handleDateRangeChange = value => {
    setLocalDateRange(value);
    if (!value) {
      return;
    }
    const [start, end] = value;
    if (start && end) {
      dispatch(setFilter(filterKeys.month.key, null));
      dispatch(setFilter(filterKeys.year.key, null));
      dispatch(setFilter(filterKeys.startDate.key, value));
    }
  };

  const commonIncludeExcludeFilterProps = {
    filterType: 'selectIncludeExclude',
    mode: 'multiple',
    tagRender,
    renderOption,
    disabled: loading,
  };
  return (
    <div>
      <Container
        title='Tradeshows Calendar'
        headerRight={
          <FilterButtonGroup>
            <Button
              style={{ flex: 1 }}
              data-testid='request-tradeshow-link'
              disabled={user.isPartnerAccount}
              tooltip={user.isPartnerAccount ? <UpgradeLicense /> : ''}
              type='seconday'
              onClick={onRequestNewTradeShowClick}
            >
              Request New Tradeshow
            </Button>
          </FilterButtonGroup>
        }
      >
        <FilterContainer>
          <Filters>
            <div style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
              Show tradeshows for
            </div>
            <YearsMenu
              years={years}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              dateRange={filterStartDate}
              onYearSelect={handleYearSelect}
              onMonthSelect={handleMonthSelect}
              onCustomSelect={handleCustomSelect}
              loading={loadingYears}
            />
            {(displayCustomDate || filterStartDate) && (
              <DatePicker
                containerStyle={{ flex: 'none' }}
                type='range'
                placeholder={['Start Date', 'End Date']}
                onChange={handleDateRangeChange}
                value={localDateRange}
              />
            )}
            <Input
              style={{ minWidth: '250px', maxWidth: '300px', flex: 1 }}
              disabled={loading}
              placeholder={filterKeys.name.description}
              value={filterName}
              data-testid='filter-name'
              onChange={value =>
                dispatch(setFilter(filterKeys.name.key, value))
              }
              allowClear
            />
            <FilterSection>
              <Filter
                filterType='token'
                style={{
                  minWidth: '300px',
                  maxWidth: '300px',
                  minHeight: '32px',
                  height: 'fit-content',
                  padding: '3px 4px 0px 3px',
                  flex: 1,
                }}
                placeholder={filterKeys.city.description}
                filterId={filterKeys.city.key}
                disabled={loading}
                includeExclude
              />
              <Filter
                {...commonIncludeExcludeFilterProps}
                style={{ minWidth: '300px', maxWidth: '300px', flex: 1 }}
                filterId={filterKeys.state.key}
                placeholder={filterKeys.state.description}
                options={states}
                remoteSearchOnClick
                optionProp={{
                  optionLabel: 'displayName',
                  optionValue: 'id',
                }}
              />
              <Filter
                {...commonIncludeExcludeFilterProps}
                style={{ minWidth: '300px', maxWidth: '300px', flex: 1 }}
                filterId={filterKeys.regionCountry.key}
                placeholder={filterKeys.regionCountry.description}
                options={countries}
              />
            </FilterSection>
            <FilterSection show={showMoreFilters}>
              <FilterSection>
                <Filter
                  {...commonIncludeExcludeFilterProps}
                  style={{ minWidth: '300px', maxWidth: '300px', flex: 1 }}
                  filterId={filterKeys.tradeshowStatus.key}
                  placeholder={filterKeys.tradeshowStatus.description}
                  options={tradeshowStatuses}
                />
                <Filter
                  {...commonIncludeExcludeFilterProps}
                  style={{ minWidth: '300px', maxWidth: '300px', flex: 1 }}
                  filterId={filterKeys.platformStatus.key}
                  placeholder={filterKeys.platformStatus.description}
                  options={tradeshowPlatformStatuses}
                />
                <Filter
                  {...commonIncludeExcludeFilterProps}
                  style={{ minWidth: '300px', maxWidth: '300px', flex: 1 }}
                  filterId={filterKeys.registeredInterest.key}
                  placeholder={filterKeys.registeredInterest.description}
                  options={registeredInterestOptions}
                />
                <Filter
                  {...commonIncludeExcludeFilterProps}
                  style={{ minWidth: '300px', maxWidth: '300px', flex: 1 }}
                  filterId={filterKeys.tradeshowEventFormat.key}
                  placeholder={filterKeys.tradeshowEventFormat.description}
                  options={tradeshowEventFormatOptions}
                />
              </FilterSection>
            </FilterSection>
          </Filters>
          <FilterActions>
            <FilterButtonGroup style={{ marginLeft: 0 }}>
              <Button
                style={{ flex: 1 }}
                type='secondary'
                data-testid='more-filters-button'
                onClick={() => setShowMoreFilters(v => !v)}
              >
                {showMoreFilters ? 'Hide Filters' : 'More Filters'}
              </Button>
              <Button
                type='secondary'
                data-testid='clear-search-button'
                onClick={() => handleClear()}
              >
                Clear
              </Button>
            </FilterButtonGroup>
            <ExportTradeshowCalendarButton
              filteredData={filteredData || []}
              filters={filters}
            />
          </FilterActions>
        </FilterContainer>
        <Table data={filteredData} loading={loading || loadingYears} />
      </Container>
      {displayTradeShowModal && !reportLoading ? (
        <RequestTradeShow
          canDisplay={displayTradeShowModal}
          setDisplay={setDisplayTradeShowModal}
          performExport={postExport}
        />
      ) : (
        <div></div>
      )}
    </div>
  );
};

export default Calendar;
